<template>
  <div class="login-form-org">
    <div class="form-group form-unit" v-if="steps === 'login'">
      <label class="form-unit__label form-unit__label--upper-sm">{{
        $t("template.email_address")
      }}</label>
      <input
        type="email"
        v-on:keyup.enter="$emit('login-user')"
        v-model="email"
        class="form-control form-unit__input"
        tabindex="1"
      />
    </div>

    <div
      class="form-group form-unit"
      v-if="steps === 'resetPassword' || steps === 'resetPasswordPhone'"
    >
      <div class="action-title">
        <p v-if="steps === 'resetPassword'" class="sign-form__description">
          {{ $t("template.reset_by_email") }}
        </p>
        <p v-else-if="steps === 'resetPasswordPhone'" class="sign-form__description">
          {{ $t("template.reset_by_sms") }}
        </p>
      </div>
      <label class="form-unit__label form-unit__label--upper-sm form-unit__label--w-link"
        >{{ $t("template.email_address") }}
        <a
          v-if="steps === 'resetPassword'"
          href="#"
          @click.prevent="$emit('steps-set', 'resetPasswordPhone')"
          tabindex="3"
          >{{ $t("template.send_sms") }}</a
        >
        <a
          v-else-if="steps === 'resetPasswordPhone'"
          href="#"
          @click.prevent="$emit('steps-set', 'resetPassword')"
          tabindex="3"
          >{{ $t("template.send_email") }}</a
        >
      </label>
      <input
        type="email"
        v-on:keyup.enter="$emit('login-user')"
        v-model="email"
        class="form-control form-unit__input"
        tabindex="1"
        @input="emailChange"
      />
      <span v-show="this.loginLocalErrors.email" class="v-help is-danger">{{
        $t("template.valid_email_is_required")
      }}</span>
    </div>

    <div class="form-group form-unit" v-if="steps === 'resetPasswordPhone'">
      <label class="form-unit__label form-unit__label--upper-sm form-unit__label--w-link"
        >{{ $t("template.phone_number") }}
      </label>
      <VueTelInput
        name="phone"
        v-model="phone"
        v-validate="{
          required: true,
          twilio_lookup: twilioLookup,
        }"
        :inputOptions="{
          styleClasses: 'form-control form-unit__input',
        }"
        mode="international"
        data-vv-validate-on="blur"
        valid-characters-only
        tabindex="4"
        @validate="validate"
        :defaultCountry="defaultCountry"
        :autoDefaultCountry="autoDefaultCountry"
      />
      <span
        v-if="vErrorsHas('phone', 'required') || vErrorsHas('phone', 'decimal')"
        class="v-help is-danger"
      >
        {{ $t("template.field_is_required") }}
      </span>
      <span v-if="twilioLookupErrorMsg" class="v-help is-danger">
        {{ twilioLookupErrorMsg }}
      </span>
    </div>

    <div class="form-group form-unit" v-if="steps === 'login'">
      <label class="form-unit__label form-unit__label--upper-sm form-unit__label--w-link">
        {{ $t("template.password") }}
        <a href="#" @click.prevent="$emit('steps-set', 'resetPassword')" tabindex="3">{{
          $t("template.forgot_password")
        }}</a>
      </label>
      <input
        type="password"
        v-on:keyup.enter="$emit('login-user')"
        v-model="password"
        class="form-control form-unit__input"
        tabindex="2"
      />
    </div>

    <div class="form-group form-unit" v-if="steps === '2facode'">
      <label class="form-unit__label form-unit__label--upper-sm">
        {{ $t("template.two_fa_vode", "Code from your Googe Authenticator") }}
      </label>
      <input
        v-on:keyup.enter="$emit('login-user')"
        v-model="twoFACode"
        class="form-control form-unit__input"
        tabindex="1"
      />
    </div>

    <div class="form-group form-unit" v-if="steps === 'emailOTP'">
      <label class="form-unit__label form-unit__label--upper-sm">
        {{ $t("template.otp_email_code_label", "Enter the code sent to your email") }}
      </label>
      <div class="tw-flex tw-gap-2 tw-justify-center tw-mt-2" style="direction: ltr">
        <input
          v-for="(digit, index) in otpCodeDigits"
          :key="index"
          :ref="`otpCodeInput${index}`"
          type="number"
          v-model="otpCodeDigits[index]"
          class="hide-arrows form-control tw-w-12 tw-h-12 tw-text-center tw-text-lg tw-font-semibold"
          maxlength="1"
          :tabindex="5 + index"
          @input="onOtpDigitInput(index, $event)"
          @keydown="onOtpDigitKeydown(index, $event)"
          @paste="onOtpPaste($event)"
        />
      </div>
      <span v-if="otpCodeError" class="v-help is-danger tw-block tw-text-center tw-mt-2">
        {{ otpCodeError }}
      </span>
    </div>

    <div class="login-message" v-if="steps === 'backToLogin' && prevstep === 'resetPassword'">
      {{ $t("template.password_reset_please_check_email") }}
    </div>
    <div
      class="login-message"
      v-else-if="steps === 'backToLogin' && prevstep === 'resetPasswordPhone'"
    >
      {{ $t("template.password_reset_please_check_phone") }}
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { mapState } from "vuex";
import { VueTelInput } from "vue-tel-input";

export default defineComponent({
  props: [
    "login",
    "steps",
    "prevstep",
    "successresetpassword",
    "stepsset",
    "stepto",
    "proceedresetpassword",
    "loginuser",
    "loginLocalErrors",
  ],

  components: {
    VueTelInput,
  },

  data() {
    return {
      phoneObject: null,
      otpCodeDigits: ["", "", "", "", "", ""],
      otpCodeError: "",
    };
  },

  watch: {
    steps(newVal) {
      if (newVal === "emailOTP") {
        this.otpCodeDigits = ["", "", "", "", "", ""];
        this.otpCodeError = "";
        this.$nextTick(() => this.focusOtpInput(0));
      }
    },
  },

  computed: {
    phone: {
      get() {
        return this.login.phone || "";
      },
      set(val) {
        this.$set(this.login, "phone", val);
      },
    },
    email: {
      get() {
        return this.login.email || "";
      },
      set(val) {
        this.$set(this.login, "email", val);
      },
    },
    password: {
      get() {
        return this.login.password || "";
      },
      set(val) {
        this.$set(this.login, "password", val);
      },
    },
    twoFACode: {
      get() {
        return this.login["2fa_code"] || "";
      },
      set(val) {
        this.$set(this.login, "2fa_code", val);
      },
    },
    ...mapState({
      defaultCountry: (s) => s.account.userCountry,
    }),
    autoDefaultCountry() {
      return this.defaultCountry === "N/A" || this.defaultCountry.length === 0;
    },
    twilioLookupErrorMsg() {
      const exist = this.vErrors.items.find(
        (el) => el.field === "phone" && el.rule === "twilio_lookup"
      );

      if (exist) {
        return this.$t("template.field_error_twilio_lookup", "Invalid or incorrect phone number");
      }

      return "";
    },
    twilioLookup() {
      return {
        valid: this.phoneObject?.valid || false,
      };
    },
  },
  methods: {
    validate(phoneObject) {
      this.phoneObject = phoneObject;

      if (!this.phoneObject?.valid) {
        this.$set(this.loginLocalErrors, "phone", true);
        return;
      }

      this.$set(this.loginLocalErrors, "phone", false);
    },
    vErrorsHas(field, rule) {
      return this.vErrors.items.find((el) => el.field === field && el.rule === rule);
    },
    focusOtpInput(index) {
      this.$nextTick(() => {
        const inputs = this.$refs[`otpCodeInput${index}`];
        if (inputs && inputs.length > 0) {
          inputs[0].focus();
        } else if (inputs) {
          inputs.focus();
        }
      });
    },
    onOtpDigitInput(index, event) {
      const value = event.target.value;
      if (!/^\d*$/.test(value)) {
        this.$set(this.otpCodeDigits, index, "");
        return;
      }
      const singleDigit = value.length > 0 ? value.charAt(value.length - 1) : "";
      this.$set(this.otpCodeDigits, index, singleDigit);
      this.otpCodeError = "";
      if (singleDigit && index < 5) {
        this.focusOtpInput(index + 1);
      }
      this.$set(this.login, "otp_code", this.otpCodeDigits.join(""));
      if (
        this.otpCodeDigits.every((digit) => digit !== "") &&
        this.otpCodeDigits.join("").length === 6
      ) {
        this.$nextTick(() => this.$emit("login-user"));
      }
    },
    onOtpDigitKeydown(index, event) {
      if (event.key === "Backspace" && !this.otpCodeDigits[index] && index > 0) {
        this.focusOtpInput(index - 1);
      }
      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        this.focusOtpInput(index - 1);
      } else if (event.key === "ArrowRight" && index < 5) {
        event.preventDefault();
        this.focusOtpInput(index + 1);
      }
    },
    onOtpPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData("text");
      const digits = pastedData.replace(/\D/g, "").slice(0, 6);
      for (let i = 0; i < 6; i++) {
        this.$set(this.otpCodeDigits, i, digits[i] || "");
      }
      this.$set(this.login, "otp_code", this.otpCodeDigits.join(""));
      const nextEmptyIndex = this.otpCodeDigits.findIndex((digit) => !digit);
      if (nextEmptyIndex !== -1) {
        this.focusOtpInput(nextEmptyIndex);
      } else if (digits.length === 6) {
        this.$nextTick(() => this.$emit("login-user"));
      }
    },
    emailChange() {
      if (this.login.email === "" || !this.validEmail(this.login.email)) {
        this.$set(this.loginLocalErrors, "email", true);
        return;
      }

      this.$set(this.loginLocalErrors, "email", false);
    },
    validEmail: function (email) {
      var re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },
  },
});
</script>

<style lang="scss" scoped></style>
